import clsx from "clsx";

import type { BoxProps } from "@/ui/atoms/Box";
import Box from "@/ui/atoms/Box";
import { Button, Icon } from "@proda-ai/ui-kit";
import Typography from "@/ui/atoms/Typography";

export type FileExtractionErrorProps = Omit<BoxProps, "children"> & {
    error: {
        errorMessage: string;
        isNetworkError?: boolean;
        isExtractionNetworkError?: boolean;
    };
    onClose: () => void;
    selectedFileName: string | null | undefined;
    resetFileUploadState?: () => void;
    onLlmReExtract?: () => void;
};

export const testIds = {
    closeButton: "FileExtractionError-closeButton",
    tryAnotherFileButton: "FileExtractionError-tryAnotherFileButton",
    llmReExtractButton: "FileExtractionError-llmReExtractButton",
    main: "FileExtractionError",
};

const FileExtractionError = (props: FileExtractionErrorProps) => {
    const {
        sx,
        className,
        error,
        selectedFileName,
        onClose,
        resetFileUploadState,
        onLlmReExtract,
        ...otherProps
    } = props;

    const isExtractionFailure = error.errorMessage.includes(
        "We encountered an error while extracting this file during upload.",
    );

    const isUploadFailure = error.isNetworkError ?? false;

    const isExtractionNetworkFailure = error.isExtractionNetworkError ?? false;

    const buttonStyles = {
        width: "100%",
        minWidth: "120px",
        padding: "0px 16px",
    };

    return (
        <Box
            data-testid={testIds.main}
            className={clsx([testIds.main, className])}
            minWidth="40vw"
            sx={{
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                ...sx,
            }}
            {...otherProps}
        >
            <Box
                id="upload-wrapper"
                sx={{
                    width: "100%",
                    height: "100%",
                    textAlign: "center",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    borderRadius: "16px",
                    transition: "border 0.3s ease-in-out",
                    padding: "32px",
                }}
            >
                {!isUploadFailure &&
                    selectedFileName != null &&
                    selectedFileName.trim() !== "" && (
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "16px",
                                padding: "16px 24px",
                                backgroundColor: "var(--proda-white-l95)",
                                borderRadius: "16px",
                                width: "100%",
                                justifyContent: "space-between",
                            }}
                        >
                            <Typography
                                variant="body3"
                                sx={{
                                    fontWeight: 700,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "16px",
                                    minWidth: 0,
                                    overflow: "hidden",
                                }}
                            >
                                <Icon
                                    icon="file-text"
                                    size={18}
                                    color="var(--proda-black-10)"
                                />
                                <span
                                    style={{
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                    }}
                                    title={selectedFileName}
                                >
                                    {selectedFileName}
                                </span>
                            </Typography>
                            <Typography
                                variant="body3"
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                    fontStyle: "italic",
                                    flexShrink: 0,
                                }}
                            >
                                <Icon
                                    icon="check-circle"
                                    size={12}
                                    color="var(--proda-neon-blue)"
                                />
                                {"File Uploaded"}
                            </Typography>
                        </Box>
                    )}
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: "16px",
                        marginY: "24px",
                    }}
                >
                    <Icon
                        icon="triangle-exclamation"
                        size={48}
                        color="var(--proda-yellow-120)"
                    />
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: "8px",
                            textAlign: "center",
                            maxWidth: "600px",
                        }}
                    >
                        <Typography
                            variant="body1"
                            sx={{
                                color: "var(--proda-black)",
                                fontWeight: 700,
                            }}
                        >
                            {isExtractionFailure
                                ? error.errorMessage
                                : isUploadFailure
                                  ? "Upload failed"
                                  : isExtractionNetworkFailure
                                    ? "Connection lost"
                                    : "An error occurred"}
                        </Typography>
                        {isUploadFailure ? (
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "16px",
                                    textAlign: "left",
                                }}
                            >
                                <Typography
                                    variant="body3"
                                    sx={{
                                        color: "var(--proda-black-10)",
                                    }}
                                >
                                    We didn&apos;t receive the upload request
                                    from your browser. This is usually caused
                                    by:
                                </Typography>
                                <Box
                                    component="ul"
                                    sx={{
                                        margin: 0,
                                        paddingLeft: "24px",
                                        color: "var(--proda-black-10)",
                                        listStyleType: "disc",
                                    }}
                                >
                                    <Typography component="li" variant="body3">
                                        a network interruption
                                    </Typography>
                                    <Typography component="li" variant="body3">
                                        a firewall or security policy blocking
                                        uploads to PRODA
                                    </Typography>
                                    <Typography component="li" variant="body3">
                                        Windows machines temporarily locking
                                        open files
                                    </Typography>
                                </Box>
                                <Typography
                                    variant="body3"
                                    sx={{
                                        color: "var(--proda-black-10)",
                                    }}
                                >
                                    Please try again. If the problem persists,
                                    copy the file to a local folder and retry.
                                    If you still encounter the problem, contact
                                    PRODA support or your IT team for
                                    assistance.
                                </Typography>
                            </Box>
                        ) : isExtractionNetworkFailure ? (
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "16px",
                                    textAlign: "center",
                                }}
                            >
                                <Typography
                                    variant="body3"
                                    sx={{
                                        color: "var(--proda-black-10)",
                                        textAlign: "center",
                                    }}
                                >
                                    The connection to PRODA was lost while
                                    processing this file. This is usually caused
                                    by a network interruption or a firewall.
                                    Please try again. If the problem persists,
                                    contact PRODA support or your IT team for
                                    assistance.
                                </Typography>
                            </Box>
                        ) : (
                            !isExtractionFailure && (
                                <Typography
                                    variant="body3"
                                    sx={{
                                        color: "var(--proda-black-10)",
                                    }}
                                >
                                    {error.errorMessage}
                                </Typography>
                            )
                        )}
                    </Box>
                </Box>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        gap: "16px",
                        flexDirection: "row",
                        alignItems: "center",
                        marginBottom: "24px",
                    }}
                >
                    <Button
                        data-testid={testIds.closeButton}
                        pattern="secondary"
                        size="large"
                        onClick={onClose}
                        style={buttonStyles}
                    >
                        Close
                    </Button>
                    {onLlmReExtract && (
                        <Button
                            data-testid={testIds.llmReExtractButton}
                            pattern="primary"
                            size="large"
                            onClick={onLlmReExtract}
                            style={buttonStyles}
                        >
                            Extract with AI
                        </Button>
                    )}
                    {resetFileUploadState && (
                        <Button
                            data-testid={testIds.tryAnotherFileButton}
                            pattern="primary"
                            size="large"
                            onClick={resetFileUploadState}
                            style={buttonStyles}
                        >
                            Try another file
                        </Button>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default FileExtractionError;
